/**
 * Abilities registry — single source of truth.
 *
 * Lives in `shared/` so server (cast pipeline, cooldown ticker, effect
 * dispatch) and client (HUD slot icons, cooldown wheels, cursor handoff)
 * read the same row. Adding a new ability = adding one row here. If you
 * find yourself editing a second file that lists ability names, you've hit
 * a leak — fix the leak. See `docs/build/data-driven-features.md`.
 *
 * Source: `docs/design/09-abilities.md` (Draft, all decisions locked
 * 2026-05-01) carries the canonical KTS-verbatim numbers with `AbilData.xml`,
 * `EffectData.xml`, and `MapScript.galaxy` line citations.
 *
 * Rollout — this slice (the `abilities-engine` worktree) seeds the registry
 * with the 3 expertise utility abilities, so the cast pipeline can be wired
 * end-to-end against minimal surface before the 16-spell tavern pool lands:
 *
 *   - MineralTap   (Mining 1)   — unit, mana 50, 30s CD; +30% mining for 30s.
 *                                  KTS `AbilData.xml:3421`,
 *                                  `MapScript.galaxy:5982-5983`.
 *   - NaturesGrace (Forestry 1) — unit, mana 50, 8s CD; tree advance 35% /
 *                                  bush restock 2. KTS `AbilData.xml:3441`.
 *   - PlantTree    (Forestry 1) — point-charge, free, 90s charge restock;
 *                                  AoE 25 dmg / r=5 + sapling-as-zone.
 *                                  KTS `MapScript.galaxy:5982-5983, 6013-6014`.
 *
 * The 16-spell tavern pool (Frost Armor, Black Hole, etc.) lands as
 * additional rows in subsequent slice tasks; the discriminated unions in
 * `ability-types.ts` already cover their targeting/cost shapes.
 *
 * Closures vs literals (per `data-driven-features.md` §"Closures vs
 * literals"): v1 uses literals only. Charge counts / cooldowns scale with
 * expertise level (Plant Tree 1→2→3 charges at Forestry L1/L3/L5; CD halved
 * at L5), but those scalings happen in the server-side cast handler reading
 * the caster's expertise — not in this registry. Encoding the formulas as
 * data here would be premature (per the two-instance rule) until a SECOND
 * ability needs the same formula.
 */

import type { AbilityDef, AbilityId } from "./ability-types";

const MINERAL_TAP: AbilityDef = {
  id: "MineralTap",
  name: "Mineral Tap",
  // KTS `Mineral`-class TargetFilter: Resource. The cast handler narrows
  // to mineral deposits specifically; the filter only pre-screens.
  targeting: { mode: "unit", range: 6, targetFilters: ["Resource"] },
  cost: { kind: "mana", amount: 50 },
  cooldownMs: 30000,
  school: "none",
  canCrit: false,
  effects: [
    { kind: "mining-buff", params: { rateBp: 13000, durationMs: 30000 } },
  ],
  // Buff lasts 30s; the tag should persist for the whole window, not a 1.5s blink.
  placeholderVfx: { kind: "unit-tag", color: 0xffe066, durationMs: 30000 },
};

const NATURES_GRACE: AbilityDef = {
  id: "NaturesGrace",
  name: "Nature's Grace",
  // KTS Tree / Bush TargetFilter: Resource. Sub-kind dispatch (tree advance
  // vs bush restock) lives in the cast handler.
  targeting: { mode: "unit", range: 6, targetFilters: ["Resource"] },
  cost: { kind: "mana", amount: 50 },
  cooldownMs: 8000,
  school: "none",
  canCrit: false,
  effects: [
    // 35% growth advance on a tree; bush branch reads `bushRestockN`.
    { kind: "forestry-grow", params: { treeAdvancePct: 35, bushRestockN: 2 } },
  ],
  placeholderVfx: { kind: "unit-tag", color: 0x59c259, durationMs: 1200 },
};

const PLANT_TREE: AbilityDef = {
  id: "PlantTree",
  name: "Plant Tree",
  // KTS baseline 1 charge at Forestry L1; scales 2 at L3, 3 at L5 in the
  // cast handler (per `MapScript.galaxy:5982-5983`). 90s restock per charge
  // is the L1 baseline; L5 halves it via Greenmother's Boon, also handler-
  // side.
  targeting: { mode: "point-charge", range: 3, chargesMax: 1, chargeRestockMs: 90000 },
  cost: { kind: "free" },
  cooldownMs: 0,
  school: "spell",
  canCrit: true,
  effects: [
    // 25 AoE damage in radius 5 + 0.65s near-freeze slow on placement.
    { kind: "aoe-damage", params: { damage: 25, radius: 5, slowDurationMs: 650 } },
    // Sapling-as-zone: 3 AoE damage every 0.5s for 12s, 100% lifesteal back.
    { kind: "spawn-sapling", params: { tickDamage: 3, tickPeriodMs: 500, durationMs: 12000, lifestealPct: 100 } },
  ],
  placeholderVfx: { kind: "point-burst", color: 0x59c259, radius: 5, durationMs: 700 },
};

// =============================================================================
// 16-spell tavern shop pool
// =============================================================================
// Source: docs/design/09-abilities.md §"v1 tavern shop spell list" (table at
// line 192). Numbers lifted verbatim from KTS AbilData.xml + EffectData.xml +
// MapScript.galaxy:48450-48700.
//
// Effect handlers for these spells haven't shipped — `dispatchAbilityEffect`
// dev-warns on unknown `kind` and the cast still completes (cost debit + CD
// set + broadcast). This is the same data-driven-deps pattern items use with
// `pendingEffects` (06-economy-gear.md D12). Adding the handler later activates
// the spell with zero edit to this file.
//
// What works today even without handlers:
//   - Spell appears in shop / debug-grant catalog
//   - Server validates targeting + range + LoS
//   - Walk-to-range orchestration runs
//   - Cost (mana / hp-sacrifice with floor) debits correctly
//   - Cooldown sets (incl. shared cooldownKey for FrostArmor ↔ GuardianSpirit)
//   - Q/W/E/R bar renders + cooldown wheel ticks
//   - HUD cast cursor arms / cancels
// What's dormant until handlers land: the *effect* (DoT tick, slow, shield, etc).

const CLAIRVOYANCE: AbilityDef = {
  id: "Clairvoyance",
  name: "Clairvoyance",
  tavern: { gold: 30 },
  // KTS Range=12 (AbilData.xml `Clairvoyance`); point cast — reveal area + slow if caster within 15.
  targeting: { mode: "point", range: 12 },
  cost: { kind: "mana", amount: 40 },
  cooldownMs: 6000,
  school: "spell",
  canCrit: false,
  effects: [
    // reveal-area is a v1 stub (no fog-of-war system). Defer to post-jam.
    { kind: "reveal-area", params: { radius: 5, durationMs: 5000 } },
    // Slow is gated on caster being within 15u of zone center (gateRadius).
    // Out-of-gate → tick skips re-apply → existing slow times out in ~1.5s.
    { kind: "slow-zone", params: { buffId: "ClairvoyanceSlowBuff", radius: 5, durationMs: 3000, gateRadius: 15 } },
  ],
  // Persistent zone — disc overrides the default point-burst.
  placeholderVfx: { kind: "zone-disc", color: 0xffe066, radius: 5, durationMs: 3000 },
};

const FROST_ARMOR: AbilityDef = {
  id: "FrostArmor",
  name: "Frost Armor",
  tavern: { gold: 40 },
  targeting: { mode: "self" },
  cost: { kind: "mana", amount: 60 },
  cooldownMs: 20500,
  // Shared 15s timer with Guardian Spirit (KTS `Cooldown Link="Abil/FrostArmor"`).
  // Casting either puts the other on the same CD.
  cooldownKey: "FrostArmor",
  school: "spell",
  canCrit: true,
  effects: [
    // ACTIVE: applies FrostArmorBuff. The buff carries (a) statMods +5 armor /
    // +5 sArmor (handled by armor-buff handler), (b) onTick draining 1 warmth/sec
    // (BUFF_BEHAVIORS), (c) reflectOnHit dealing 1 magic dmg + 3 warmth drain to
    // physical attackers (BUFF_BEHAVIORS + installBuffCombatHooks). The other two
    // effect rows below are reference-only — the work happens via FrostArmorBuff.
    { kind: "armor-buff", params: { armor: 5, spellArmor: 5, durationMs: 30000 } },
    // ACTIVE-VIA-BUFF: see armor-buff comment. installBuffCombatHooks subscribes to
    // combat.postDamage and consults BUFF_BEHAVIORS[id].reflectOnHit on the target.
    { kind: "reflect-on-hit", params: { magicDamage: 1, warmthDrain: 3 } },
    // ACTIVE-VIA-BUFF: BUFF_BEHAVIORS[FrostArmorBuff].onTick drains 1 warmth/sec.
    { kind: "warmth-drain-self", params: { perSec: 1 } },
  ],
  placeholderVfx: { kind: "self-aura", color: 0x4a90d4, durationMs: 30000 },
};

const BLACK_HOLE: AbilityDef = {
  id: "BlackHole",
  name: "Black Hole",
  tavern: { gold: 40 },
  // KTS BlackHoleSearch radius 5; cast range 8.
  targeting: { mode: "point", range: 8 },
  cost: { kind: "mana", amount: 60 },
  cooldownMs: 25000,
  school: "spell",
  canCrit: true,
  effects: [
    // params:
    //   buffId        — slow buff id to re-apply each tick. Read by slow-zone handler.
    //   radius        — zone radius in world units.
    //   durationMs    — zone lifetime; tick loop expires after this.
    //   gateRadius    — (optional) caster-proximity gate radius. Omitted → no gate.
    //
    // KTS slow pct + AS slow live on the buff's statMods (BLACK_HOLE_SLOW_BUFF).
    // Kept here as documentation comments only — runtime reads the buff.
    { kind: "slow-zone", params: { buffId: "BlackHoleSlowBuff", radius: 5, durationMs: 4000 } },
    { kind: "destroy-missiles", params: { radius: 5 } },
  ],
  // Persistent zone — disc overrides the default point-burst.
  placeholderVfx: { kind: "zone-disc", color: 0xb800b8, radius: 5, durationMs: 4000 },
};

const STANCE: AbilityDef = {
  id: "Stance",
  name: "Stance",
  tavern: { gold: 40 },
  targeting: { mode: "self" },
  // KTS source: Unit,KoboldWarrior,LifeMax*.12 (floor) / *.15 (sacrifice).
  cost: { kind: "hp-sacrifice", percentMaxHp: 15, floorPercent: 12 },
  cooldownMs: 14000,
  school: "physical",
  canCrit: true,
  // Single effect: applies StanceShieldBuff with shield = 3.5 × sacrificed HP.
  // The leftover-shield ring damage AoE + 45% heal refund are wired on the
  // buff itself via `BUFF_BEHAVIORS.StanceShieldBuff.onExpire`, so the row
  // doesn't carry a second `expire-ring-damage` effect — KTS-Heroic
  // multiplier (1.3× at baseline endurance) lives next to that callback.
  effects: [
    { kind: "shield-self", params: { buffId: "StanceShieldBuff", shieldPerHpSacrificed: 3.5, percentMaxHp: 15 } },
  ],
  placeholderVfx: { kind: "self-aura", color: 0xe8e8e8, durationMs: 8000 },
};

const BACKSTAB: AbilityDef = {
  id: "Backstab",
  name: "Backstab",
  tavern: { gold: 40 },
  targeting: { mode: "self" },
  cost: { kind: "mana", amount: 60 },
  cooldownMs: 8000,
  school: "physical",
  // Built-in crits via the rider; canCrit flag on the spell row itself doesn't apply.
  canCrit: false,
  effects: [
    // Rider per-swing numbers (+18 dmg, forceCrit) live on
    // BUFF_BEHAVIORS["BackstabBuff"].onSwingResolve. nextN/armingMs are
    // documentation-mirrored from BUFF_DEFS["BackstabBuff"].durationMs.
    // bonusDamage/guaranteedCrit/biologicalOnly retained as documentation
    // (data-driven-deps placeholders); single source of truth is the buff.
    { kind: "attack-rider", params: { buffId: "BackstabBuff", nextN: 3, armingMs: 8000, bonusDamage: 18, guaranteedCrit: 1, biologicalOnly: 1 } },
  ],
  placeholderVfx: { kind: "self-aura", color: 0xff4d4d, durationMs: 8000 },
};

const CELESTIAL_HYMN: AbilityDef = {
  id: "CelestialHymn",
  name: "Celestial Hymn",
  tavern: { gold: 40 },
  // KTS PriestF — only channel spell in v1. Movement allowed; only damage breaks.
  targeting: { mode: "channel", periodMs: 2000, durationMs: 10000 },
  cost: { kind: "mana", amount: 40 },
  cooldownMs: 10000,
  school: "spell",
  canCrit: false,
  effects: [
    { kind: "channel-heal", params: { healPerTick: 20, healWilCoeff: 1.0, radius: 6, breakOnDamageTaken: 1 } },
  ],
  placeholderVfx: { kind: "channel-orb", color: 0xffd966 },
};

const SIPHON_LIFE: AbilityDef = {
  id: "SiphonLife",
  name: "Siphon Life",
  tavern: { gold: 50 },
  targeting: { mode: "self" },
  cost: { kind: "mana", amount: 70 },
  cooldownMs: 8000,
  school: "spell",
  canCrit: true,
  effects: [
    // Rider per-swing numbers (caster +16 HP, hunger ±8 transfer) live on
    // BUFF_BEHAVIORS["SiphonLifeBuff"].onSwingResolve. nextN/armingMs mirror
    // BUFF_DEFS["SiphonLifeBuff"].durationMs. lifestealHp/drainHunger retained
    // as documentation; single source of truth is the buff.
    { kind: "attack-rider", params: { buffId: "SiphonLifeBuff", nextN: 3, armingMs: 8000, lifestealHp: 16, drainHunger: 8 } },
  ],
  placeholderVfx: { kind: "self-aura", color: 0xa040ff, durationMs: 8000 },
};

const NEUTRALIZING_WAVE: AbilityDef = {
  id: "NeutralizingWave",
  name: "Neutralizing Wave",
  tavern: { gold: 50 },
  // 2 charges, 12s recharge each. Spec range "(per range)" — using KTS default 6.
  targeting: { mode: "point-charge", range: 6, chargesMax: 2, chargeRestockMs: 12000 },
  cost: { kind: "mana", amount: 55 },
  cooldownMs: 12000,
  school: "spell",
  canCrit: true,
  effects: [
    { kind: "beam-aoe-damage", params: { damage: 35, wilCoeff: 0.9, radius: 5 } },
    { kind: "neutralize-debuff", params: { radius: 5, blockPhysicalNonRanged: 1, spellDamageTakenPct: 130, durationMs: 6000 } },
  ],
  placeholderVfx: { kind: "beam", color: 0x88ccff, durationMs: 600 },
};

const GUARDIAN_SPIRIT: AbilityDef = {
  id: "GuardianSpirit",
  name: "Guardian Spirit",
  tavern: { gold: 50, resourceKey: "radiantGem" },
  targeting: { mode: "self" },
  cost: { kind: "mana", amount: 90 },
  cooldownMs: 15000,
  // Charge-shared CD with Frost Armor — single 15s timer across both spells.
  cooldownKey: "FrostArmor",
  school: "spell",
  canCrit: false,
  effects: [
    { kind: "regen-self", params: { perSec: 2, intCoefficient: 1, durationMs: 15000 } },
    { kind: "sacrifice-on-ally-low", params: { triggerHpPct: 40, healCoefficient: 100, radius: 6 } },
  ],
  placeholderVfx: { kind: "self-aura", color: 0xe0e0ff, durationMs: 15000 },
};

const AFFLICTION: AbilityDef = {
  id: "Affliction",
  name: "Affliction",
  tavern: { gold: 60 },
  targeting: { mode: "unit", range: 6, targetFilters: ["Biological", "Enemy"] },
  cost: { kind: "mana", amount: 80 },
  cooldownMs: 13500,
  school: "spell",
  canCrit: true,
  effects: [
    // Per-tick damage + stamina-drain rider live in BUFF_DEFS / BUFF_BEHAVIORS
    // under "AfflictionBuff" — single source of truth for the numbers. The
    // handler at handlers/dot-magic.ts just calls applyBuff with the target
    // and the source's sessionId.
    { kind: "dot-magic", params: { buffId: "AfflictionBuff" } },
    // Spread-on-death: scan nearest enemy in range, re-apply with one fewer charge.
    { kind: "spread-on-death", params: { range: 6, chargesRemaining: 1 } },
  ],
  placeholderVfx: { kind: "unit-tag", color: 0x88aa44, durationMs: 8000 },
};

const ARCANE_NOVA: AbilityDef = {
  id: "ArcaneNova",
  name: "Arcane Nova",
  tavern: { gold: 60 },
  targeting: { mode: "point", range: 6 },
  cost: { kind: "mana", amount: 55 },
  cooldownMs: 11000,
  school: "spell",
  canCrit: true,
  effects: [
    // Line of strikes: N spaced impact points, each a radius-5 damage instance
    // with -25% per repeat-hit; max 4 stacks via ArcaneNovaTargetCooldown.
    { kind: "lightning-line", params: { strikesN: 5, perStrikeDamage: 25, perStrikeWilCoeff: 0.9, perStrikeRadius: 5, repeatPenaltyPct: 25, maxStacksPerTarget: 4 } },
  ],
  placeholderVfx: { kind: "point-burst", color: 0xa040ff, radius: 5, durationMs: 700 },
};

const FLURRY: AbilityDef = {
  id: "Flurry",
  name: "Flurry",
  tavern: { gold: 60 },
  // Leap to target point. Range 6; KTS Flurry uses point-target leap.
  targeting: { mode: "point", range: 6 },
  cost: { kind: "hp-sacrifice", percentMaxHp: 10, floorPercent: 5 },
  cooldownMs: 11000,
  school: "physical",
  canCrit: true,
  effects: [
    { kind: "leap-aoe", params: { damage: 40, attackDamageMult: 1.25, radiusOfPath: 2.75, requirePathable: 1 } },
  ],
  placeholderVfx: { kind: "point-burst", color: 0xff4d4d, radius: 3, durationMs: 600 },
};

const SHADOW_CLOAK: AbilityDef = {
  id: "ShadowCloak",
  name: "Shadow Cloak",
  tavern: { gold: 70, resourceKey: "shadowstone" },
  // KTS toggle (no CD). Break events listed at the engine surface so
  // `handleToggleBreakEvent` flips OFF on basic-attack (swing.ts) or
  // potion-throw (items/use.ts). Spell-side rider damage (+50 magic) +
  // companion buff lifecycle live in `handlers/stealth.ts` +
  // `BUFF_BEHAVIORS["ShadowCloakBuff"]`. Drain ramp is handler-side too —
  // the engine `drain` field stays unset (ramp doesn't fit the flat-rate
  // surface; see `toggle.ts` JSDoc on the BUFF_BEHAVIORS.onTick split).
  targeting: { mode: "toggle", breakOn: ["attack", "potion-throw"] },
  // Initial 60 mana then 1/sec drain after 5s grace, capping at 10/sec — drain is handler-side.
  cost: { kind: "mana", amount: 60 },
  cooldownMs: 0,
  school: "spell",
  canCrit: true,
  effects: [
    { kind: "stealth", params: { moveSpeedPct: 20, breakOnAttackBonusDmg: 50, postGraceDrainPerSec: 1, drainCapPerSec: 10, graceMs: 5000 } },
  ],
  placeholderVfx: { kind: "toggle-aura", color: 0x222266 },
};

const SPECTRAL_EDICT: AbilityDef = {
  id: "SpectralEdict",
  name: "Spectral Edict",
  tavern: { gold: 70, resourceKey: "shadowstone" },
  // KTS quirk: 0-mana cost (no <Vital index="Energy"> line). 25s CD is the brake.
  targeting: { mode: "point", range: 5.5 },
  cost: { kind: "free" },
  cooldownMs: 25000,
  school: "spell",
  canCrit: true,
  effects: [
    // Two spinning beams: tickPeriodMs=100, durationMs=1650 per beam, push 1.5u/s outward.
    // Per-second tooltip damage = ((WIL - 18) × 0.25 + 8) / 0.1 = 80 dps per beam at WIL=18.
    { kind: "spectral-beam", params: { beamCount: 2, tickPeriodMs: 100, durationMs: 1650, pushPerSec: 1.5, baseDamagePerTick: 8, wilCoefficient: 0.25, cannotDamageBuildings: 1 } },
  ],
  placeholderVfx: { kind: "beam", color: 0xa040ff, durationMs: 1650 },
};

const WARCRY: AbilityDef = {
  id: "Warcry",
  name: "Warcry",
  tavern: { gold: 70, resourceKey: "shadowstone" },
  targeting: { mode: "self" },
  cost: { kind: "mana", amount: 50 },
  cooldownMs: 30000,
  school: "spell",
  canCrit: false,
  effects: [
    { kind: "cleanse-self-and-allies", params: { radius: 6, msBonusPct: 25, durationMs: 8000, periodicCleanseMs: 2000 } },
  ],
  placeholderVfx: { kind: "self-aura", color: 0xffe066, durationMs: 8000 },
};

const POWER_WORD_SHIELD: AbilityDef = {
  id: "PowerWordShield",
  name: "Power Word: Shield",
  tavern: { gold: 70, resourceKey: "shadowstone" },
  // KTS priestD; ally-only (MapScript.galaxy:50337-50352).
  targeting: { mode: "unit", range: 6, targetFilters: ["Ally"] },
  cost: { kind: "mana", amount: 20 },
  cooldownMs: 5000,
  school: "spell",
  canCrit: false,
  // shield = base + max(0, willpower − 18) × perWillpower. Baseline-WIL caster
  // shields the ally for `base`; high-WIL casters scale up. 60s duration is
  // strategic-pre-fight, not reactive (PowerWordShieldBuff lives on the buff
  // map until absorbed or expired).
  effects: [
    { kind: "shield-ally", params: { buffId: "PowerWordShieldBuff", base: 40, perWillpower: 1 } },
  ],
  placeholderVfx: { kind: "unit-tag", color: 0xe8e8e8, durationMs: 60000 },
};

// B5 expertise toggle abilities (Mining t4 / Foraging t4). Granted (not bought),
// bound to a Q/W/E/R slot. Counter-gated: an activity counter (off-schema,
// server/src/expertise/toggle-counters.ts) accumulates from mining swings / animal
// kills, drains −1/sec while lit, and auto-offs at 0. 3s toggle cooldown. The
// `drain.resource: "counter"` tells the engine the spell owns its drain (the
// AdrenalineRush/SageMode buff onTick does it) — see abilities/toggle.ts.
const ADRENALINE_RUSH: AbilityDef = {
  id: "AdrenalineRush",
  name: "Adrenaline Rush",
  // Mining L20 toggle (AbilData.xml:3504, BehaviorData.xml:6782): +10% move speed,
  // zero stamina drain while running, Frostbite immunity. Counter-gated uptime.
  targeting: { mode: "toggle", drain: { resource: "counter", perSecond: 1 } },
  cost: { kind: "free" },
  cooldownMs: 3000,
  school: "none",
  canCrit: false,
  effects: [
    { kind: "expertise-toggle", params: { kind: "adrenaline", buffId: "AdrenalineRushBuff" } },
  ],
  placeholderVfx: { kind: "toggle-aura", color: 0xff7a33 },
};

const SAGE_MODE: AbilityDef = {
  id: "SageMode",
  name: "Sage Mode",
  // Foraging L20 toggle (AbilData.xml:1147, BehaviorData.xml:3550): true sight,
  // +20% attack speed, +2% life/energy regen. ("See enemy tracks" deferred,
  // Decision C.) Counter-gated uptime.
  targeting: { mode: "toggle", drain: { resource: "counter", perSecond: 1 } },
  cost: { kind: "free" },
  cooldownMs: 3000,
  school: "none",
  canCrit: false,
  effects: [
    { kind: "expertise-toggle", params: { kind: "sage", buffId: "SageModeBuff" } },
  ],
  placeholderVfx: { kind: "toggle-aura", color: 0x66dd88 },
};

/**
 * The full ability registry. Keyed by `AbilityId` (string). Lookup is the
 * single point of truth — `ABILITIES[id] !== undefined` is the runtime
 * existence check.
 */
export const ABILITIES: Readonly<Record<AbilityId, AbilityDef>> = {
  // Expertise utility unlocks (3) — handlers shipped this slice.
  [MINERAL_TAP.id]:        MINERAL_TAP,
  [NATURES_GRACE.id]:      NATURES_GRACE,
  [PLANT_TREE.id]:         PLANT_TREE,
  [ADRENALINE_RUSH.id]:    ADRENALINE_RUSH,
  [SAGE_MODE.id]:          SAGE_MODE,
  // Tavern shop pool (16) — data shipped, effect handlers dormant per data-driven-deps.
  [CLAIRVOYANCE.id]:       CLAIRVOYANCE,
  [FROST_ARMOR.id]:        FROST_ARMOR,
  [BLACK_HOLE.id]:         BLACK_HOLE,
  [STANCE.id]:             STANCE,
  [BACKSTAB.id]:           BACKSTAB,
  [CELESTIAL_HYMN.id]:     CELESTIAL_HYMN,
  [SIPHON_LIFE.id]:        SIPHON_LIFE,
  [NEUTRALIZING_WAVE.id]:  NEUTRALIZING_WAVE,
  [GUARDIAN_SPIRIT.id]:    GUARDIAN_SPIRIT,
  [AFFLICTION.id]:         AFFLICTION,
  [ARCANE_NOVA.id]:        ARCANE_NOVA,
  [FLURRY.id]:             FLURRY,
  [SHADOW_CLOAK.id]:       SHADOW_CLOAK,
  [SPECTRAL_EDICT.id]:     SPECTRAL_EDICT,
  [WARCRY.id]:             WARCRY,
  [POWER_WORD_SHIELD.id]:  POWER_WORD_SHIELD,
};

/**
 * One-line shop blurbs for the tavern spell-shop UI. Keyed by `AbilityId`.
 * These are presentational copy (the player-facing "what does this do"), kept
 * here in shared so the client doesn't grow a parallel description table — the
 * tavern modal reads `SPELL_SHOP_BLURBS[id]` straight from this single source.
 * Only the 16 buyable spells need an entry; the 3 expertise utilities + 2
 * toggles aren't sold at the shop so they're omitted (the modal only iterates
 * abilities whose `def.tavern` is present anyway).
 *
 * Effect text is the *intended* KTS behaviour from docs/design/09-abilities.md
 * §"v1 tavern shop spell list". A few effects are still dormant (no handler
 * yet — Neutralizing Wave; Clairvoyance's reveal); those casts debit cost + CD
 * but do nothing visible until the handler lands. The blurb describes the
 * design intent regardless, so it stays correct when the handler ships.
 */
export const SPELL_SHOP_BLURBS: Readonly<Record<string, string>> = {
  Clairvoyance:     "Reveal an area and slow enemy kobolds caught near you.",
  FrostArmor:       "Armor up; attackers take frost damage and lose warmth.",
  BlackHole:        "Drop a zone that slows enemies and eats their missiles.",
  Stance:           "Sacrifice HP for a big shield that bursts on expiry.",
  Backstab:         "Your next 3 hits are guaranteed crits for +18 damage.",
  CelestialHymn:    "Channel a heal-over-time to yourself and nearby allies.",
  SiphonLife:       "Your next 3 hits steal 16 HP and 8 hunger each.",
  NeutralizingWave: "Beam that damages and neutralizes enemies in a radius.",
  GuardianSpirit:   "Regenerate, then sacrifice to heal a low-HP ally.",
  Affliction:       "Damage-over-time that spreads to a new enemy on death.",
  ArcaneNova:       "Call a line of lightning strikes across a target area.",
  Flurry:           "Leap to a point, slashing enemies along the path.",
  ShadowCloak:      "Turn invisible and faster; break-attack hits for +50.",
  SpectralEdict:    "Two spinning beams shred enemies at a point — free to cast.",
  Warcry:           "Cleanse debuffs and boost move speed for you and allies.",
  PowerWordShield:  "Shield an ally for 60 seconds.",
};

/**
 * Player-facing blurbs for EVERY ability, used by the Q/W/E/R hover tooltip.
 * Spreads the 16 shop blurbs and adds the 5 expertise-granted abilities (3
 * utilities + 2 toggles) that never appear in the tavern shop. Single source so
 * the client tooltip reads `ABILITY_BLURBS[id]` without a parallel table.
 */
export const ABILITY_BLURBS: Readonly<Record<string, string>> = {
  ...SPELL_SHOP_BLURBS,
  MineralTap:     "Channel a mineral deposit to mine 30% faster for 30s.",
  NaturesGrace:   "Advance a tree's growth, or restock a berry/herb bush.",
  PlantTree:      "Plant a sapling that damages and slows nearby enemies.",
  AdrenalineRush: "Toggle: +10% move speed, no stamina drain, frostbite immunity.",
  SageMode:       "Toggle: +20% attack speed and faster life/energy regen.",
};
